import { Injectable, OnDestroy } from '@angular/core';
import { ClaimKeys } from 'app/core/constants/claim-keys';
import { FeatureFlagStore } from 'app/core/stores/feature-flag.store';
import { AuthStore } from 'app/core/stores/auth.store';
import { environment } from 'src/environments/environment';
import { initialize, LDClient, LDFlagSet, LDContext } from 'launchdarkly-js-client-sdk';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { User } from '@auth0/auth0-angular';

type UserWithClaims = User & {
  [key: string]: unknown;
};

@Injectable({ providedIn: 'root' })
export class FeatureFlagStoreService implements OnDestroy {
  private _flags: Record<string, unknown> = {};

  private ldClient: LDClient | null = null;
  private context!: LDContext;
  private contextSignature: string | null = null;
  private readonly ready$ = new ReplaySubject<boolean>(1);
  private readyStateTimeoutId: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly authStore: AuthStore,
    private readonly featureFlagStore: FeatureFlagStore
  ) {
    this.init();
  }

  private init(): void {
    combineLatest([this.authStore.user$, this.authStore.organizationId$])
      .pipe(
        filter(([u]) => !!u),
        take(1)
      )
      .subscribe(([u, orgId]) => {
        const authUser = u as UserWithClaims;

        if (!authUser.sub) return;

        const appMeta = authUser[ClaimKeys.app_metadata] as
          | { organization_id?: string; office_id?: string }
          | undefined;

        this.context = this.buildContext(authUser, orgId ?? appMeta?.organization_id);
        this.contextSignature = JSON.stringify(this.context);

        this.ldClient = initialize(environment.launchDarkly.clientKey, this.context);

        this.ldClient.on('ready', () => {
          this._flags = this.ldClient!.allFlags();
          this.onFlagChange();
          this.ready$.next(true);

          if (this.readyStateTimeoutId) {
            clearTimeout(this.readyStateTimeoutId);
            this.readyStateTimeoutId = undefined;
          }
        });

        this.ldClient.on('change', (flags: LDFlagSet) => this.onFlagChange(flags));
      });

    this.authStore.user$.pipe(filter((u): u is User => u != null)).subscribe(authUser => {
      if (!this.ldClient) return;

      const u = authUser as UserWithClaims;
      const appMeta = u[ClaimKeys.app_metadata] as { organization_id?: string; office_id?: string } | undefined;

      const nextContext = this.buildContext(u, appMeta?.organization_id);
      const nextSignature = JSON.stringify(nextContext);

      // user$ replays on subscribe and re-emits on every token refresh; identify only
      // when the context actually changed, otherwise this re-evaluates the same flags.
      if (nextSignature === this.contextSignature) return;

      this.context = nextContext;
      this.contextSignature = nextSignature;

      this.ldClient.identify(this.context).then(flags => this.onFlagChange(flags));
    });
  }

  ngOnDestroy(): void {
    if (this.readyStateTimeoutId) clearTimeout(this.readyStateTimeoutId);
    this.ready$.complete();
    this.ldClient?.close();
    this.ldClient = null;
  }

  get currentAndPreviousFlags(): LDFlagSet {
    return { ...this._flags };
  }

  get featureFlags$(): Observable<Record<string, boolean | string | number>> {
    return combineLatest([this.featureFlagStore.flags$, this.ready$]).pipe(
      filter(([, isReady]) => isReady),
      map(([f]) => f)
    );
  }

  get isReady$(): Observable<boolean> {
    return this.ready$.asObservable();
  }

  variation<T extends boolean | string | number>(flagKey: string, defaultValue: T): T {
    const value = this.featureFlagStore.currentFlags[flagKey];
    return value === undefined ? defaultValue : (value as T);
  }

  private buildContext(authUser: UserWithClaims, organizationId?: string): LDContext {
    const appMeta = authUser[ClaimKeys.app_metadata] as { organization_id?: string; office_id?: string } | undefined;
    const email = (authUser[ClaimKeys.email] ?? authUser.email) as string | undefined;

    return {
      kind: 'user',
      key: email ?? authUser.sub!,
      name: authUser.name ?? email,
      email,
      custom: {
        userId: authUser.sub,
        email,
        organizationId: organizationId ?? appMeta?.organization_id,
        officeId: appMeta?.office_id,
      },
    };
  }

  private onFlagChange(flags?: LDFlagSet): void {
    if (flags) {
      this._flags = { ...this._flags, ...flags };
    }

    const currentFlags: Record<string, boolean | string | number> = {};

    Object.entries(this._flags).forEach(([key, value]) => {
      if (value && typeof value === 'object' && 'current' in value) {
        currentFlags[key] = (value as { current: boolean | string | number }).current;
      } else {
        currentFlags[key] = value as boolean | string | number;
      }
    });

    this.featureFlagStore.setFlags(currentFlags);
  }
}
